// A picker for the dashboard's forms, in place of the native <select> (whose menu is the system's, not ours): the
// trigger looks like the text field beside it, the choices open under it on the menus' surface, the chosen one
// checked. The list is position: fixed from the trigger's rect, so a dialog or a scrolling card can't clip it.

import { IconCheck, IconSelector } from '@tabler/icons-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useCloseWhenMoved } from './closeWhenMoved';

export type SelectOption = { value: string; label: string };

export function Select({ id, value, options, onChange, testid }: { id?: string; value: string; options: SelectOption[]; onChange: (v: string) => void; testid?: string }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const listId = useId();
  const chosen = options.findIndex((o) => o.value === value);

  const show = () => {
    const r = trigger.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    setActive(Math.max(0, chosen));
    setOpen(true);
  };
  const hide = (refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };
  const pick = (i: number) => {
    hide();
    if (options[i] && options[i].value !== value) onChange(options[i].value);
  };

  // under the trigger, or over it when there is no room
  useLayoutEffect(() => {
    if (!open || !pos || !list.current || !trigger.current) return;
    const h = list.current.getBoundingClientRect().height;
    const r = trigger.current.getBoundingClientRect();
    const top = r.bottom + 4 + h > window.innerHeight - 8 && r.top - h - 4 > 8 ? r.top - h - 4 : r.bottom + 4;
    if (Math.abs(top - pos.top) > 0.5) setPos({ ...pos, top });
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;
    list.current?.focus();
    const away = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!list.current?.contains(t) && !trigger.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);
  useCloseWhenMoved(open, trigger, () => setOpen(false));

  // keep the active choice in view
  useEffect(() => {
    if (open) list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const onListKey = (e: KeyboardEvent) => {
    const last = options.length - 1;
    if (e.key === 'ArrowDown') setActive((i) => Math.min(last, i + 1));
    else if (e.key === 'ArrowUp') setActive((i) => Math.max(0, i - 1));
    else if (e.key === 'Home') setActive(0);
    else if (e.key === 'End') setActive(last);
    else if (e.key === 'Enter' || e.key === ' ') pick(active);
    else if (e.key === 'Escape') hide(); // the list only: a dialog around it stays open
    else if (e.key === 'Tab') return hide(false);
    else return;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        className="select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-testid={testid}
        data-value={value}
        onClick={() => (open ? hide() : show())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            show();
          }
        }}
      >
        <span className="select-value">{options[chosen]?.label ?? ''}</span>
        <IconSelector size={16} stroke={1.5} aria-hidden="true" />
      </button>
      {open && pos && (
        <div
          ref={list}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={id}
          aria-activedescendant={`${listId}-${active}`}
          className="profile-pop select-pop"
          style={{ position: 'fixed', top: pos.top, left: pos.left, right: 'auto', width: pos.width }}
          onKeyDown={onListKey}
        >
          {options.map((o, i) => (
            <div
              key={o.value}
              id={`${listId}-${i}`}
              data-i={i}
              role="option"
              aria-selected={o.value === value}
              className={`profile-item select-item${i === active ? ' is-active' : ''}`}
              data-testid={testid ? `${testid}-opt-${o.value || 'none'}` : undefined}
              onPointerMove={() => setActive(i)}
              onClick={() => pick(i)}
            >
              <span className="select-label">{o.label}</span>
              <span className="select-check">{o.value === value && <IconCheck size={16} stroke={2} aria-hidden="true" />}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
